import React, { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Form } from '@unform/web';
import { isEqual } from 'lodash';
import PropTypes from 'prop-types';
import * as Yup from 'yup';

import { Spinner, Accordion, SelectDataInTable } from '~/components';
import { Select, Input, Autocomplete } from '~/components/Form';
import {
  DESCARGA_NF,
  SOBRAS_E_FALTAS,
  CUSTOMIZACAO_RAIZEN,
  MODULO_20_GRAUS,
  MODULO_FERROVIA,
  MODULOFOTO,
  MODULO_CUSTOMIZACAO_RUMO,
  SKID,
  LOCALIDADE_ABASTECIMENTO,
  MODULO_CUSTOMIZACAO_VLI,
} from '~/constants/modules';
import { t, validCharacters, can } from '~/helpers';
import { storePosto, updatePosto } from '~/store/modules/posto/actions';

import * as S from './styles';

export default function CreateOrUpdate({ reference, data, create }) {
  const dispatch = useDispatch();

  const { client } = useSelector((state) => state.user.profile);
  const { loading, find, params } = useSelector((state) => state.posto);

  const [yesOrNo, setYesOrNo] = useState(0);
  const [
    yesOrNoAmostraCompartimento,
    setYesOrNoAmostraCompartimento,
  ] = useState(0);
  const [useInfoAbastPosto, setUseInfoAbastPosto] = useState(0);
  const [solicFrentSegundaLibPosto, setSolicFrentSegundaLibPosto] = useState(0);

  const [produtonfes, setProdutonfes] = useState([]);

  const initials = useMemo(
    () =>
      create
        ? { turnoretroativoposto: 'S', ...data }
        : { turnoretroativoposto: 'S', ...find, ...data },
    [create, data, find]
  );

  const enableControla20 = yesOrNo === 1;
  const enableSolicFrentSegundaLibPosto = Number(useInfoAbastPosto) === 1;

  const estados = [
    { label: 'Acre', value: 'AC' },
    { label: 'Alagoas', value: 'AL' },
    { label: 'Amapá', value: 'AP' },
    { label: 'Amazonas', value: 'AM' },
    { label: 'Bahia', value: 'BA' },
    { label: 'Ceará', value: 'CE' },
    { label: 'Distrito Federal', value: 'DF' },
    { label: 'Espírito Santo', value: 'ES' },
    { label: 'Goiás', value: 'GO' },
    { label: 'Maranhão', value: 'MA' },
    { label: 'Mato Grosso', value: 'MT' },
    { label: 'Mato Grosso do Sul', value: 'MS' },
    { label: 'Minas Gerais', value: 'MG' },
    { label: 'Pará', value: 'PA' },
    { label: 'Paraíba', value: 'PB' },
    { label: 'Paraná', value: 'PR' },
    { label: 'Pernambuco', value: 'PE' },
    { label: 'Piauí', value: 'PI' },
    { label: 'Rio de Janeiro', value: 'RJ' },
    { label: 'Rio Grande do Norte', value: 'RN' },
    { label: 'Rio Grande do Sul', value: 'RS' },
    { label: 'Rondônia', value: 'RO' },
    { label: 'Roraima', value: 'RR' },
    { label: 'Santa Catarina', value: 'SC' },
    { label: 'São Paulo', value: 'SP' },
    { label: 'Sergipe', value: 'SE' },
    { label: 'Tocantins', value: 'TO' },
  ];

  async function handleYesOrNo(value) {
    await reference.current.setFieldValue('controla20grausposto', value);
    await setYesOrNo(value);
  }

  async function handleYesOrNoamostraCompartimento(value) {
    await reference.current.setFieldValue(
      'operamostracompartimentossemreceb20posto',
      value
    );
    await setYesOrNoAmostraCompartimento(value);
  }

  async function handleUseInfoAbastPosto(value) {
    await reference.current.setFieldValue('utilinfoabastposto', value);
    await setUseInfoAbastPosto(value);
  }

  async function handleSolicFrentSegundaLibPosto(value) {
    await reference.current.setFieldValue('solicfrentsegundlibposto', value);
    await setSolicFrentSegundaLibPosto(value);
  }

  async function handleSubmit(param) {
    try {
      const schema = Yup.object().shape({
        codigoposto: Yup.string()
          .required(t('s.geral.validacao.campoobrigatorio'))
          .max(10, t('s.geral.validacao.tamanhomaximo').replace('%s', 10))
          .test(
            'characters-invalid',
            t('s.configuracao.geral.naopodecontercaracterespecial'),
            (value) => validCharacters(value)
          ),
        codigoauxiliarposto: Yup.string()
          .required(t('s.geral.validacao.campoobrigatorio'))
          .max(20, t('s.geral.validacao.tamanhomaximo').replace('%s', 20))
          .test(
            'characters-invalid',
            t('s.configuracao.geral.naopodecontercaracterespecial'),
            (value) => validCharacters(value)
          ),
        nomeposto: Yup.string()
          .required(t('s.geral.validacao.campoobrigatorio'))
          .max(50, t('s.geral.validacao.tamanhomaximo').replace('%s', 50))
          .test(
            'characters-invalid',
            t('s.configuracao.geral.naopodecontercaracterespecial'),
            (value) => validCharacters(value)
          ),
        nomeabreviadoposto: Yup.string()
          .required(t('s.geral.validacao.campoobrigatorio'))
          .max(10, t('s.geral.validacao.tamanhomaximo').replace('%s', 10))
          .test(
            'characters-invalid',
            t('s.configuracao.geral.naopodecontercaracterespecial'),
            (value) => validCharacters(value)
          ),
        ajustehoraposto: Yup.number('Informe um número.')
          .required(t('s.geral.validacao.campoobrigatorio'))
          .min(-12, t('s.geral.validacao.valorminimo').replace('%s', -12))
          .max(12, t('s.geral.validacao.valormaximo').replace('%s', 12)),
        statusposto: Yup.string().required(
          t('s.geral.validacao.campoobrigatorio')
        ),
        turnoretroativoposto: Yup.string().required(
          t('s.geral.validacao.campoobrigatorio')
        ),
        tipodescargaposto: can(DESCARGA_NF)
          ? Yup.string().required(t('s.geral.validacao.campoobrigatorio'))
          : Yup.string().nullable(),
        transfereturnoposto: can(SOBRAS_E_FALTAS)
          ? Yup.string().required(t('s.geral.validacao.campoobrigatorio'))
          : Yup.string().nullable(),
        cnpjposto: Yup.string().max(
          20,
          t('s.geral.validacao.tamanhomaximo').replace('%s', 20)
        ),
        depositoposto: Yup.string()
          .nullable()
          .max(10, t('s.geral.validacao.tamanhomaximo').replace('%s', 10)),
        centroposto: Yup.string()
          .nullable()
          .max(10, t('s.geral.validacao.tamanhomaximo').replace('%s', 10)),
        controlapitstopposto: can(MODULO_FERROVIA)
          ? Yup.string().required(t('s.geral.validacao.campoobrigatorio'))
          : Yup.string().nullable(),
        habilitafotonotafiscalposto:
          can(DESCARGA_NF) && can(MODULOFOTO)
            ? Yup.string().required(t('s.geral.validacao.campoobrigatorio'))
            : Yup.string().nullable(),
        controla20grausposto: can(MODULO_20_GRAUS)
          ? Yup.number()
              .required(t('s.geral.validacao.campoobrigatorio'))
              .typeError(t('s.geral.validacao.campoobrigatorio'))
          : Yup.number(),
        opercompartimentosposto:
          can(DESCARGA_NF) && can(MODULO_20_GRAUS)
            ? Yup.number()
                .required(t('s.geral.validacao.campoobrigatorio'))
                .typeError(t('s.geral.validacao.campoobrigatorio'))
            : Yup.number(),
        operamostracompartimentossemreceb20posto:
          can(DESCARGA_NF) && can(MODULO_20_GRAUS)
            ? Yup.number()
                .required(t('s.geral.validacao.campoobrigatorio'))
                .typeError(t('s.geral.validacao.campoobrigatorio'))
            : Yup.number(),
        operrecebimento20posto:
          can(DESCARGA_NF) && can(MODULO_20_GRAUS) && yesOrNo === 1
            ? Yup.number()
                .required(t('s.geral.validacao.campoobrigatorio'))
                .typeError(t('s.geral.validacao.campoobrigatorio'))
            : Yup.number(),
        operabastecimento20posto:
          can(MODULO_20_GRAUS) && yesOrNo === 1
            ? Yup.number()
                .required(t('s.geral.validacao.campoobrigatorio'))
                .typeError(t('s.geral.validacao.campoobrigatorio'))
            : Yup.number(),
        enderecoposto: can(SOBRAS_E_FALTAS)
          ? Yup.string()
              .nullable()
              .max(100, t('s.geral.validacao.tamanhomaximo').replace('%s', 100))
          : Yup.string().nullable(),
        municipioposto: can(SOBRAS_E_FALTAS)
          ? Yup.string()
              .nullable()
              .max(40, t('s.geral.validacao.tamanhomaximo').replace('%s', 40))
          : Yup.string().nullable(),
        ufposto: can(SOBRAS_E_FALTAS)
          ? Yup.string()
              .nullable()
              .max(2, t('s.geral.validacao.tamanhomaximo').replace('%s', 2))
          : Yup.string().nullable(),
        inscricaoestadualposto: can(SOBRAS_E_FALTAS)
          ? Yup.string()
              .nullable()
              .max(20, t('s.geral.validacao.tamanhomaximo').replace('%s', 20))
          : Yup.string().nullable(),
        inscricaomunicipalposto: can(SOBRAS_E_FALTAS)
          ? Yup.string()
              .nullable()
              .max(15, t('s.geral.validacao.tamanhomaximo').replace('%s', 15))
          : Yup.string().nullable(),
        nomedistribuidoraposto: can(SOBRAS_E_FALTAS)
          ? Yup.string()
              .nullable()
              .max(50, t('s.geral.validacao.tamanhomaximo').replace('%s', 50))
          : Yup.string().nullable(),
        idcorredor: can(MODULO_FERROVIA)
          ? Yup.number()
              .required(t('s.geral.validacao.campoobrigatorio'))
              .typeError(t('s.geral.validacao.campoobrigatorio'))
              .min(1, t('s.geral.validacao.campoobrigatorio'))
          : Yup.number().nullable(),
        lubrificaoficinaposto: can(MODULO_CUSTOMIZACAO_VLI)
          ? Yup.string().required(t('s.geral.validacao.campoobrigatorio'))
          : Yup.string().nullable(),
      });

      await schema.validate(param, {
        abortEarly: false,
      });

      const listProductsNF = produtonfes.map((item) => ({
        idcliente: client,
        idposto: initials.idposto,
        cnpjpostocnpj: item.cnpjpostocnpj,
      }));

      const objSend = {
        ...initials,
        ...param,
        idcliente: client,
        tipoposto: 'P',

        controlapitstopposto: can(MODULO_FERROVIA)
          ? param.controlapitstopposto
          : 'N',
        // controla20grausposto: can(MODULO_20_GRAUS) ? param.controla20grausposto : 0,
        controla20grausposto: can(MODULO_20_GRAUS) ? yesOrNo : 0,
        opercompartimentosposto:
          can(DESCARGA_NF) && can(MODULO_20_GRAUS)
            ? param.opercompartimentosposto
            : 0,
        operamostracompartimentossemreceb20posto:
          can(DESCARGA_NF) && can(MODULO_20_GRAUS)
            ? yesOrNoAmostraCompartimento
            : 0,
        operrecebimento20posto:
          can(DESCARGA_NF) && can(MODULO_20_GRAUS) && yesOrNo === 1
            ? param.operrecebimento20posto
            : 0,
        operabastecimento20posto:
          can(MODULO_20_GRAUS) && yesOrNo === 1
            ? param.operabastecimento20posto
            : 0,
        enderecoposto: can(SOBRAS_E_FALTAS) ? param.enderecoposto : '',
        municipioposto: can(SOBRAS_E_FALTAS) ? param.municipioposto : '',
        ufposto: can(SOBRAS_E_FALTAS) ? param.ufposto : '',
        inscricaoestadualposto: can(SOBRAS_E_FALTAS)
          ? param.inscricaoestadualposto
          : '',
        inscricaomunicipalposto: can(SOBRAS_E_FALTAS)
          ? param.inscricaomunicipalposto
          : '',
        nomedistribuidoraposto: can(SOBRAS_E_FALTAS)
          ? param.nomedistribuidoraposto
          : '',
        postocnpjs: listProductsNF,
        idcorredor: can(MODULO_FERROVIA) ? param.idcorredor : null,
        idlocalidade: can(LOCALIDADE_ABASTECIMENTO)
          ? Number(param.idlocalidade) || null
          : null,
        utilinfoabastposto: Number(useInfoAbastPosto || 0),
        solicfrentsegundlibposto:
          Number(useInfoAbastPosto || 0) === 1
            ? Number(solicFrentSegundaLibPosto || 0)
            : 0,
      };

      if (create) {
        await dispatch(storePosto(objSend, params));
      } else {
        await dispatch(updatePosto(objSend, params));
      }
    } catch (err) {
      const validationErrors = {};

      if (err instanceof Yup.ValidationError) {
        err.inner.forEach((error) => {
          validationErrors[error.path] = error.message.replace(
            `${error.path} `,
            ''
          );
        });

        reference.current.setErrors(validationErrors);
      }
    }
  }

  useEffect(() => {
    if (!loading) {
      reference.current.setData(initials);
    }
  }, [loading, initials]);

  useEffect(() => {
    if (!create && find.controla20grausposto) {
      setYesOrNo(find.controla20grausposto || 0);
    }

    if (!create && find.operamostracompartimentossemreceb20posto) {
      setYesOrNoAmostraCompartimento(
        find.operamostracompartimentossemreceb20posto || 0
      );
    }

    if (!create) {
      setUseInfoAbastPosto(find.utilinfoabastposto || 0);
      setSolicFrentSegundaLibPosto(find.solicfrentsegundlibposto || 0);
    }

    console.log(JSON.stringify(find));

    if (!create && find.postocnpjs) {
      setProdutonfes(
        find.postocnpjs.map((item) => ({
          cnpjpostocnpj: item.cnpjpostocnpj,
        }))
      );
    }
  }, [find, create]);

  const cellsCompartiments = useMemo(() => [
    {
      id: 'cnpjpostocnpj',
      label: t('s.configuracao.posto.cnpjpostocnpj'),
    },
  ]);

  const schemaValidate = useMemo(
    () => ({
      cnpjpostocnpj: Yup.string()
        .required(t('s.geral.validacao.campoobrigatorio'))
        .max(20, t('s.geral.validacao.valormaximo').replace('%s', 20))
        .test(
          'produto-valido',
          t('s.geral.validacao.idprodutoNFInvalido'),
          (value) => {
            if (!produtonfes || produtonfes.length === 0) {
              return true;
            }
            return (
              !produtonfes.some((item) => item.idproduto === value) ||
              value === ''
            );
          }
        ),
    }),
    [produtonfes]
  );

  const isFields = useMemo(() => [
    {
      id: 'cnpjpostocnpj',
      label: t('s.configuracao.posto.cnpjpostocnpj'),
      type: 'text',
    },
  ]);

  async function validateForm(value) {
    if (!find.postocnpjs) {
      return;
    }
    if (isEqual(value, produtonfes)) {
      return;
    }
    if (value.length === 0 && find.postocnpjs.length > 0) {
      setProdutonfes([]);
      return;
    }
    setProdutonfes(value);
  }

  const accordions = useMemo(
    () => [
      {
        id: 'cnpjpostocnpj',
        title: t('s.geral.cadastro.cadcnpjpostocnpj'),
        component: () => (
          <SelectDataInTable
            icons={false}
            checkbox={false}
            showPagination={false}
            paginate={99999}
            noUpdate
            size="small"
            variant="contained"
            position="row"
            onChanged={(value) => {
              validateForm(value);
            }}
            unique="cnpjpostocnpj"
            maxWidth="xs"
            initials={produtonfes}
            fields={isFields}
            schema={schemaValidate}
            cells={cellsCompartiments}
            titleModal={t('s.geral.cadastro.cadcnpjpostocnpj')}
          />
        ),
      },
    ],
    [produtonfes]
  );

  return (
    <>
      {loading ? (
        <div style={{ height: '14rem', width: '50rem' }}>
          <Spinner loading />
        </div>
      ) : (
        <Form ref={reference} initialData={initials} onSubmit={handleSubmit}>
          <Input name="codigoposto" label={t('s.configuracao.posto.idposto')} />

          <Input
            name="nomeposto"
            disabled={can(CUSTOMIZACAO_RAIZEN)}
            label={t('s.configuracao.posto.nomeposto')}
          />

          <Input
            name="codigoauxiliarposto"
            disabled={can(CUSTOMIZACAO_RAIZEN)}
            label={t('s.configuracao.posto.codigoauxiliarposto')}
          />

          <Input
            name="nomeabreviadoposto"
            label={t('s.configuracao.posto.nomeabreviadoposto')}
          />

          <Input
            name="ajustehoraposto"
            label={t('s.configuracao.posto.ajustehoraposto')}
            type="number"
          />

          {can(LOCALIDADE_ABASTECIMENTO) && (
            <Autocomplete
              name="idlocalidade"
              label="nomelocalidade"
              unique="idlocalidade"
              title={t('s.configuracao.posto.localidade')}
              url="localidades"
              onSerialize={({ items }) =>
                items.map((item) => ({
                  ...item,
                  nomelocalidade: `${item.nomelocalidade} [${item.idlocalidade}]`,
                }))
              }
              params={{
                wherefield: ['statuslocalidade'],
                valuewherefield: ['L'],
                typewherefield: ['EQUALS'],
              }}
            />
          )}

          <Select
            name="utilinfoabastposto"
            label={t('s.configuracao.posto.utilizainfoabastandamento')}
            options={[
              { value: 0, label: t('s.geral.opcoes.nao') },
              { value: 1, label: t('s.geral.opcoes.sim') },
            ]}
            onChange={({ target }) => handleUseInfoAbastPosto(target.value)}
            defaultValue={create ? 0 : initials.utilinfoabastposto || 0}
          />

          {enableSolicFrentSegundaLibPosto && (
            <Select
              name="solicfrentsegundlibposto"
              label={t('s.configuracao.posto.solicitafrentistasegundalibposto')}
              options={[
                { value: 0, label: t('s.geral.opcoes.nao') },
                { value: 1, label: t('s.geral.opcoes.sim') },
              ]}
              onChange={({ target }) =>
                handleSolicFrentSegundaLibPosto(target.value)
              }
              defaultValue={create ? 0 : initials.solicfrentsegundlibposto || 0}
            />
          )}

          {can(DESCARGA_NF) && (
            <Select
              name="tipodescargaposto"
              label={t('s.configuracao.posto.tipodescargaposto')}
              options={[
                { value: '1', label: t('s.geral.opcoes.descarganftanque') },
                { value: '2', label: t('s.geral.opcoes.descarganf') },
              ]}
            />
          )}

          {can(SOBRAS_E_FALTAS) && (
            <Select
              name="transfereturnoposto"
              label={t('s.configuracao.posto.transfereturnoposto')}
              options={[
                { value: 0, label: t('s.geral.opcoes.nao') },
                { value: 1, label: t('s.geral.opcoes.sim') },
              ]}
            />
          )}

          <Select
            name="turnoretroativoposto"
            label={t('s.configuracao.posto.turnoretroativoposto')}
            options={[
              { value: 'S', label: t('s.geral.opcoes.sim') },
              { value: 'N', label: t('s.geral.opcoes.nao') },
            ]}
            defaultValue={create ? 'S' : initials.turnoretroativoposto}
          />

          <Input
            name="depositoposto"
            label={t('s.geral.camposistema.depositoposto')}
          />

          <Input
            name="centroposto"
            label={t('s.geral.camposistema.centroposto')}
          />

          <Input name="cnpjposto" label={t('s.configuracao.posto.cnpjposto')} />

          {can(DESCARGA_NF) && can(MODULO_20_GRAUS) && (
            <>
              <Select
                name="opercompartimentosposto"
                label={t('s.configuracao.posto.opercompartimentos')}
                options={[
                  { value: 0, label: t('s.geral.opcoes.nao') },
                  { value: 1, label: t('s.geral.opcoes.sim') },
                ]}
              />

              <Select
                name="operamostracompartimentossemreceb20posto"
                label={t(
                  's.configuracao.posto.operamostracompartimentossemreceb20posto'
                )}
                options={[
                  { value: 0, label: t('s.geral.opcoes.nao') },
                  { value: 1, label: t('s.geral.opcoes.sim') },
                ]}
                onChange={({ target }) =>
                  handleYesOrNoamostraCompartimento(target.value)
                }
                // defaultValue={create ? 0 : initials.operamostracompartimentossemreceb20posto}
              />
            </>
          )}

          {/* {can(MODULO_20_GRAUS) && (
            <Select
              name="controla20grausposto"
              label={t('s.configuracao.posto.controla20graus')}
              options={[
                { value: 0, label: t('s.geral.opcoes.nao') },
                { value: 1, label: t('s.geral.opcoes.sim') },
              ]}
            />
          )} */}

          {can(MODULO_20_GRAUS) && (
            <Select
              name="controla20grausposto"
              label={t('s.configuracao.posto.controla20graus')}
              options={[
                { value: 0, label: t('s.geral.opcoes.nao') },
                { value: 1, label: t('s.geral.opcoes.sim') },
              ]}
              onChange={({ target }) => handleYesOrNo(target.value)}
              // defaultValue={create ? 0 : initials.controla20grausposto}
            />
          )}

          {enableControla20 && (
            <>
              {can(DESCARGA_NF) && can(MODULO_20_GRAUS) && (
                <Select
                  name="operrecebimento20posto"
                  label={t('s.configuracao.posto.operrecebimento20')}
                  options={[
                    { value: 0, label: t('s.geral.opcoes.nao') },
                    { value: 1, label: t('s.geral.opcoes.sim') },
                  ]}
                />
              )}

              {can(MODULO_20_GRAUS) && (
                <Select
                  name="operabastecimento20posto"
                  label={t('s.configuracao.posto.operabastecimento20')}
                  options={[
                    { value: 0, label: t('s.geral.opcoes.nao') },
                    { value: 1, label: t('s.geral.opcoes.sim') },
                  ]}
                />
              )}
            </>
          )}

          {can(MODULO_FERROVIA) && (
            <Select
              name="controlapitstopposto"
              label={t('s.configuracao.posto.controlapitstopposto')}
              options={[
                { value: 'N', label: t('s.geral.opcoes.nao') },
                { value: 'S', label: t('s.geral.opcoes.sim') },
              ]}
            />
          )}

          {can(SOBRAS_E_FALTAS) && (
            <>
              <Input
                name="enderecoposto"
                label={t('s.configuracao.posto.enderecoposto')}
              />
              <Input
                name="municipioposto"
                label={t('s.configuracao.posto.municipioposto')}
              />
              <Select
                name="ufposto"
                label={t('s.configuracao.posto.ufposto')}
                options={estados}
              />
              <Input
                name="inscricaoestadualposto"
                label={t('s.configuracao.posto.inscricaoestadualposto')}
              />
              <Input
                name="inscricaomunicipalposto"
                label={t('s.configuracao.posto.inscricaomunicipalposto')}
              />
              <Input
                name="nomedistribuidoraposto"
                label={t('s.configuracao.posto.nomedistribuidoraposto')}
              />
            </>
          )}

          {can(DESCARGA_NF) && can(MODULOFOTO) && (
            <Select
              name="habilitafotonotafiscalposto"
              label={t('s.configuracao.posto.habilitafotonotafiscalposto')}
              options={[
                { value: 'N', label: t('s.geral.opcoes.nao') },
                { value: 'S', label: t('s.geral.opcoes.sim') },
              ]}
            />
          )}

          {can(DESCARGA_NF) && can(MODULO_CUSTOMIZACAO_RUMO) && (
            <Select
              name="habilitainspesaodescargaposto"
              label={t('s.configuracao.posto.habilitainspesaodescargaposto')}
              options={[
                { value: 'N', label: t('s.geral.opcoes.nao') },
                { value: 'S', label: t('s.geral.opcoes.sim') },
              ]}
            />
          )}

          {can(DESCARGA_NF) && can(SKID) && (
            <Select
              name="habilitaskidposto"
              label={t('s.configuracao.posto.habilitaskidposto')}
              options={[
                { value: 'N', label: t('s.geral.opcoes.nao') },
                { value: 'S', label: t('s.geral.opcoes.sim') },
              ]}
            />
          )}

          {can(MODULO_FERROVIA) && (
            <Autocomplete
              name="idcorredor"
              label="nomecorredor"
              url="corredor"
              unique="idcorredor"
              title={t('Corredor')}
            />
          )}

          {can(MODULO_CUSTOMIZACAO_VLI) && (
            <Select
              name="lubrificaoficinaposto"
              label={t('s.configuracao.posto.lubrificaoficinaposto')}
              options={[
                { value: 'N', label: t('s.geral.opcoes.nao') },
                { value: 'S', label: t('s.geral.opcoes.sim') },
              ]}
            />
          )}

          <Select
            name="statusposto"
            label={t('s.configuracao.posto.statusposto')}
            options={[
              { value: 'L', label: t('s.geral.opcoes.liberado') },
              { value: 'B', label: t('s.geral.opcoes.bloqueado') },
              { value: 'C', label: t('s.geral.opcoes.cancelado') },
            ]}
          />

          <S.Wrapper>
            <Accordion accordions={accordions} />
          </S.Wrapper>
        </Form>
      )}
    </>
  );
}

CreateOrUpdate.propTypes = {
  reference: PropTypes.objectOf(PropTypes.any).isRequired,
  create: PropTypes.bool.isRequired,
  data: PropTypes.objectOf(PropTypes.any),
};

CreateOrUpdate.defaultProps = {
  data: {},
};
